import { useMemo, useRef, useState } from 'react';
import type { ChartPoint } from '../../lib/prices';

const W = 339;
const H = 117;

function smoothPath(pts: { x: number; y: number }[]): string {
  if (pts.length < 2) return '';
  let d = `M ${pts[0]!.x} ${pts[0]!.y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i]!;
    const p1 = pts[i + 1]!;
    const cx = (p0.x + p1.x) / 2;
    d += ` C ${cx} ${p0.y}, ${cx} ${p1.y}, ${p1.x} ${p1.y}`;
  }
  return d;
}

interface Props {
  /** Real price series (from CoinGecko). Empty while loading. */
  data: ChartPoint[];
  loading?: boolean;
  /** Crosshair readout — the price + date at the dragged point. */
  onHover?: (info: { priceUsd: number; date: string } | null) => void;
}

/** Draggable price chart driven by real market data — tap and drag to read the
 *  price + date at any point. Blue line + gradient area, crosshair dot. */
export function PriceChart({ data, loading, onHover }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [active, setActive] = useState<number | null>(null);

  // Whether the whole series spans less than ~2 days (→ show time, else date).
  const intraday =
    data.length > 1 ? data[data.length - 1]!.t - data[0]!.t < 2 * 864e5 : false;

  const { pts, area, line } = useMemo(() => {
    if (data.length < 2) return { pts: [], area: '', line: '' };
    const prices = data.map((d) => d.p);
    const lo = Math.min(...prices);
    const hi = Math.max(...prices);
    const pad = 10;
    const pts = data.map((d, i) => ({
      x: (i / (data.length - 1)) * W,
      y: pad + (1 - (d.p - lo) / (hi - lo || 1)) * (H - pad * 2),
    }));
    const line = smoothPath(pts);
    const area = `${line} L ${W} ${H} L 0 ${H} Z`;
    return { pts, area, line };
  }, [data]);

  const dateAt = (i: number) => {
    const d = new Date(data[i]!.t);
    return intraday
      ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  };

  function pick(clientX: number) {
    const svg = svgRef.current;
    if (!svg || data.length < 2) return;
    const rect = svg.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * W;
    const i = Math.max(0, Math.min(data.length - 1, Math.round((x / W) * (data.length - 1))));
    setActive(i);
    onHover?.({ priceUsd: data[i]!.p, date: dateAt(i) });
  }
  function end() {
    setActive(null);
    onHover?.(null);
  }

  const cur = active != null ? pts[active]! : null;

  if (data.length < 2) {
    // Loading / no data — flat baseline placeholder.
    return (
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-[117px] w-full">
        <line
          x1="0"
          y1={H / 2}
          x2={W}
          y2={H / 2}
          stroke="rgb(var(--accent))"
          strokeWidth="2"
          strokeLinecap="round"
          opacity={loading ? 0.35 : 0.15}
          className={loading ? 'animate-pulse' : undefined}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    );
  }

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="h-[117px] w-full touch-none"
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        pick(e.clientX);
      }}
      onPointerMove={(e) => active != null && pick(e.clientX)}
      onPointerUp={end}
      onPointerLeave={end}
    >
      <defs>
        <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(var(--accent))" stopOpacity="0.22" />
          <stop offset="100%" stopColor="rgb(var(--accent))" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#chartFill)" />
      <path
        d={line}
        fill="none"
        stroke="rgb(var(--accent))"
        strokeWidth="2.5"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      {cur && (
        <>
          <line x1={cur.x} y1="0" x2={cur.x} y2={H} stroke="rgb(var(--accent))" strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
          <circle cx={cur.x} cy={cur.y} r="6" fill="rgb(var(--accent))" stroke="rgb(var(--card))" strokeWidth="2.5" />
        </>
      )}
    </svg>
  );
}
